import { PageLayout } from '../../../core/types/Typesetter';
import { parsePageCacheKey } from './CacheKeyGenerator';
import { matchesDocHash } from './CacheKeyMatcher';
import { sortAscending } from '../utils';

/**
 * CacheStatisticsCalculator - Utility for calculating page cache statistics
 *
 * Provides functions for analyzing and reporting on the state of the page cache,
 * including cache size, document hash, and cached page indices. Used for
 * debugging, monitoring, and cache management decisions.
 */
export class CacheStatisticsCalculator {
  /**
   * Calculate cache statistics from page cache and document hash
   *
   * This method analyzes the current page cache to provide statistics about
   * cached pages for a specific document. It extracts page indices that belong
   * to the current document and sorts them for consistent reporting.
   *
   * @param pageCache - The page cache map (key: cacheKey, value: PageLayout)
   * @param currentDocHash - Current document hash for filtering cache entries
   * @returns Object containing cache size, document hash, and sorted page indices
   */
  static getCacheStats(
    pageCache: Map<string, PageLayout>,
    currentDocHash: string
  ): { size: number; docHash: string; pages: number[] } {
    const pages = Array.from(pageCache.keys())
      .map(key => parsePageCacheKey(key))
      .filter((parsed) => matchesDocHash(parsed, currentDocHash))
      .map(parsed => parsed.pageIndex)
      .sort(sortAscending);

    return {
      size: pageCache.size,
      docHash: currentDocHash,
      pages
    };
  }
}
